import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, Minus, Trophy, Target, Flame, Sparkles } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis, XAxis } from "recharts";
import { variation, bestOf, hasDirection, semanticsOf } from "@/lib/testSemantics";

const TYPE_LABELS = { salto: "Salti", forza: "Forza" };
const TYPE_ICONS = { salto: "🦘", forza: "💪" };
const FASE1_TYPES = ["salto", "forza"];

// Interpretazione solo con semantica definita (lib/testSemantics)
const trend = (c, p, test) => { const v = variation(c, p, test); return v ? { ...v, better: v.improved === true } : null; };

function AthleteExerciseCard({ test, entries, goal }) {
  const sorted = [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const values = sorted.map((r) => r.best);
  const latest = sorted[sorted.length - 1];
  const previous = sorted[sorted.length - 2];
  const pb = bestOf(values, test);
  const isPBLatest = pb != null && latest.best === pb && values.length > 1;
  const tr = previous ? trend(latest.best, previous.best, test) : null;
  const TI = tr?.icon;
  const chartData = sorted.map((r) => ({ d: r.date, v: r.best }));
  const directed = hasDirection(test);
  const hib = semanticsOf(test) === "HIGHER_IS_BETTER";

  // Goal progress (solo con semantica direzionale)
  const goalTarget = directed ? (goal?.final_value ?? goal?.ai_proposed_value) : null;
  const startForGoal = goal ? (previous?.best ?? latest.best) : null;
  let progressPct = null;
  if (goal && goalTarget != null && startForGoal != null) {
    const total = Math.abs(goalTarget - startForGoal) || 1;
    const done = hib ? (latest.best - startForGoal) : (startForGoal - latest.best);
    progressPct = Math.max(0, Math.min(100, (done / total) * 100));
  }
  const distanceToGoal = goal && goalTarget != null ? (hib ? goalTarget - latest.best : latest.best - goalTarget) : null;

  return (
    <div data-testid={`athlete-ex-${test.id}`} className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
      <div className={`px-5 py-3 border-b border-zinc-100 flex items-center gap-2 ${isPBLatest ? "bg-gradient-to-r from-emerald-50 to-amber-50" : "bg-zinc-50"}`}>
        <span className="text-2xl">{TYPE_ICONS[test.test_type] || "🏆"}</span>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold truncate">{test.name}</div>
          <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">{TYPE_LABELS[test.test_type] || test.test_type} · {test.unit}</div>
        </div>
        {isPBLatest && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-amber-500 text-white flex items-center gap-1"><Trophy className="w-3 h-3" />Nuovo PB!</span>}
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Ultimo</div>
            <div className="font-mono text-2xl font-bold">{latest.best}</div>
            <div className="text-[10px] text-zinc-400 font-mono">{test.unit} · {latest.date}</div>
          </div>
          <div className="border-x border-zinc-100">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{pb != null ? "Il tuo record" : "Ultimo valore"}</div>
            <div className={`font-mono text-2xl font-bold ${pb != null ? "text-emerald-700" : "text-zinc-700"}`}>{pb ?? latest.best}</div>
            <div className="text-[10px] text-zinc-400 font-mono">{test.unit}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Prima</div>
            <div className="font-mono text-2xl font-bold text-zinc-500">{previous?.best ?? "—"}</div>
            {tr && (
              <div className={`flex items-center justify-center gap-1 text-xs font-mono font-semibold ${tr.color}`} data-testid={`athlete-variation-${test.id}`}>
                {TI && <TI className="w-3.5 h-3.5" />}{tr.delta > 0 ? "+" : ""}{tr.pct}%
              </div>
            )}
          </div>
        </div>

        {tr && tr.improved != null && (
          <div className={`text-center text-sm font-medium py-2 rounded ${tr.better ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`}>
            {tr.better && <><Flame className="w-4 h-4 inline mr-1" />Stai migliorando! Continua così.</>}
            {!tr.better && "Puoi fare di più, il prossimo test è tuo!"}
          </div>
        )}
        {tr && tr.improved == null && tr.delta === 0 && <div className="text-center text-sm font-medium py-2 rounded bg-zinc-100 text-zinc-600">Prestazione stabile</div>}

        {goal && goalTarget != null && (
          <div className="bg-gradient-to-r from-[#0052FF]/5 to-[#0052FF]/10 border border-[#0052FF]/30 rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">🎯</span>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider text-[#0052FF] font-mono font-semibold">Obiettivo</div>
                <div className="font-mono text-xl font-bold text-[#0052FF]">{goalTarget} {test.unit}</div>
              </div>
              {goal.final_target_date && <div className="text-[10px] text-zinc-500 font-mono text-right">entro<br /><span className="font-semibold text-zinc-700">{goal.final_target_date}</span></div>}
            </div>
            <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-[#0052FF]/20">
              <div className="h-full bg-[#0052FF]" style={{ width: `${progressPct || 0}%` }} />
            </div>
            <div className="flex items-center justify-between text-[10px] font-mono">
              <span className="text-zinc-500">{progressPct ? progressPct.toFixed(0) : 0}% completato</span>
              {distanceToGoal != null && distanceToGoal > 0 && <span className="text-[#0052FF] font-semibold">mancano {Math.abs(distanceToGoal).toFixed(2)} {test.unit}</span>}
              {distanceToGoal != null && distanceToGoal <= 0 && <span className="text-emerald-600 font-semibold">🏆 Obiettivo raggiunto!</span>}
            </div>
            {goal.final_motivation && <div className="text-xs text-zinc-600 italic mt-1">"{goal.final_motivation}"</div>}
          </div>
        )}

        {values.length >= 2 && (
          <div className="h-24 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
                <XAxis dataKey="d" hide />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ fontSize: 11, padding: "4px 8px", fontFamily: "monospace" }} formatter={(v) => [`${v} ${test.unit}`, "valore"]} />
                <Line type="monotone" dataKey="v" stroke="#0052FF" strokeWidth={3} dot={{ r: 3, fill: "#fff", strokeWidth: 2 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        <div className="text-[10px] text-zinc-400 text-center font-mono">{entries.length} {entries.length === 1 ? "test svolto" : "test svolti"}</div>
      </div>
    </div>
  );
}

export default function AthleteTestsView() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const [results, setResults] = useState([]);
  const [athlete, setAthlete] = useState(null);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    (async () => {
      const [t, a, r] = await Promise.all([api.get("/tests"), api.get("/athletes?include_archived=1"), api.get("/test-results")]);
      setTests(t.data.filter((x) => !x.archived && FASE1_TYPES.includes(x.test_type)));
      const me = (a.data || []).find((x) => x.user_id === user?.id);
      setAthlete(me || null);
      if (me) {
        setResults(r.data.filter((x) => x.athlete_id === me.id));
        try {
          const g = await api.get(`/test-goals?athlete_id=${me.id}`);
          setGoals((g.data || []).filter((x) => x.status === "approved" || x.status === "modified"));
        } catch (_) {}
      }
    })();
  }, [user]);

  const byExercise = useMemo(() => {
    const map = {};
    results.forEach((r) => { (map[r.test_id] = map[r.test_id] || []).push(r); });
    return map;
  }, [results]);

  const grouped = useMemo(() => {
    const g = { salto: [], forza: [] };
    Object.entries(byExercise).forEach(([tid, entries]) => {
      const test = tests.find((x) => x.id === tid);
      if (!test) return;
      (g[test.test_type] = g[test.test_type] || []).push({ test, entries });
    });
    Object.keys(g).forEach((k) => g[k].sort((a, b) => a.test.name.localeCompare(b.test.name)));
    return g;
  }, [byExercise, tests]);

  const totalExercises = Object.values(grouped).reduce((s, l) => s + l.length, 0);
  const totalPBs = Object.values(grouped).reduce((s, l) => s + l.filter(({ test, entries }) => {
    const vals = entries.map((r) => r.best);
    const pb = bestOf(vals, test);
    return pb != null && vals.length > 1 && entries.sort((a, b) => (b.date || "").localeCompare(a.date || ""))[0].best === pb;
  }).length, 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Ciao {athlete?.first_name || user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-zinc-500 mt-1">I tuoi progressi in CSP Lab. Continua a spingere!</p>
      </div>

      {!athlete && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto text-amber-600 mb-2" />
          <div className="font-semibold text-amber-900">Il tuo profilo atleta non è ancora collegato</div>
          <div className="text-sm text-amber-800 mt-1">Chiedi allo staff di associare il tuo account al profilo atleta per vedere i tuoi risultati.</div>
        </div>
      )}

      {athlete && totalExercises === 0 && (
        <div className="bg-white border border-zinc-200 rounded-md p-10 text-center text-zinc-500">
          Nessun test ancora registrato. I tuoi risultati appariranno qui appena il tecnico avrà inserito i primi dati.
        </div>
      )}

      {athlete && totalExercises > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Esercizi svolti</div>
              <div className="font-mono text-3xl font-bold mt-1">{totalExercises}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono flex items-center gap-1"><Trophy className="w-3 h-3" />Record attivi</div>
              <div className="font-mono text-3xl font-bold text-amber-600 mt-1">{totalPBs}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Test totali</div>
              <div className="font-mono text-3xl font-bold mt-1">{results.length}</div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-md p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Categorie</div>
              <div className="font-mono text-3xl font-bold mt-1">{Object.values(grouped).filter((l) => l.length > 0).length}</div>
            </div>
          </div>

          {["salto", "forza"].map((type) => grouped[type]?.length > 0 && (
            <div key={type} className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{TYPE_ICONS[type]}</span>
                <h2 className="font-heading text-2xl font-bold tracking-tight">{TYPE_LABELS[type]}</h2>
                <span className="text-xs text-zinc-500 font-mono">{grouped[type].length}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {grouped[type].map(({ test, entries }) => {
                  const goal = goals.find((g) => g.test_id === test.id);
                  return <AthleteExerciseCard key={test.id} test={test} entries={entries} goal={goal} />;
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
